import React, { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

const API_BASE_URL = "https://api.checkmark.mx/";
const ACCESS_TOKEN =
  "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

const MODULOS = [
  { name: "dashboard", label: "Dashboard", selectable: false },
  { name: "analytics", label: "Analytics", selectable: true },
  { name: "form-wizards", label: "Ventas", selectable: true },
  { name: "deals-detail", label: "Deals", selectable: false },
  { name: "deals-inbox", label: "Inbox", selectable: false },
  { name: "deals-compose", label: "Compose", selectable: false },
  { name: "deals-create", label: "Deal Crear", selectable: false },
  { name: "deals-request", label: "Crear solicitud", selectable: true },
  { name: "deals-requests", label: "Resumen", selectable: true },
  { name: "deals-charts", label: "Graficas", selectable: true },
  { name: "deals-ranking", label: "Ranking", selectable: true },
  { name: "deals-invoiced", label: "Facturacion", selectable: true },
  { name: "deals-update", label: "Update", selectable: true },
  { name: "deals-analytics", label: "Deals analytics", selectable: true },
  { name: "deals-tools", label: "Ofertas", selectable: false },
  { name: "deals-contract", label: "Contrato", selectable: false },
  { name: "pos", label: "Inventario", selectable: false },
  { name: "rendimiento", label: "Rendimiento", selectable: true },
  { name: "admin", label: "Admin", selectable: true },
  {
    name: "babilonia-users-admin",
    label: "Usuarios Babilonia",
    selectable: true,
  },
];

const safeArray = (value) => {
  if (Array.isArray(value)) {
    return value.map(String);
  }

  if (typeof value === "string" && value.trim() !== "") {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.map(String);
      }
    } catch (error) {
      return [];
    }
  }

  return [];
};

const normalizeText = (value = "") =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const getResultMessage = (result, fallback = "Ocurrió un error.") => {
  if (result?.message) return result.message;
  if (result?.error?.description) return result.error.description;
  return fallback;
};

const getInitials = (name = "") => {
  const parts = String(name || "")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "U";

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

const formatDate = (dateValue) => {
  if (!dateValue) return "-";

  const safeDate = String(dateValue).replace(" ", "T");
  const date = new Date(safeDate);

  if (Number.isNaN(date.getTime())) return dateValue;

  return date.toLocaleDateString("es-MX", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const isActiveUser = (activo) => String(activo ?? "1") === "1";

const makeExcelSafeText = (value = "") => {
  const text = String(value ?? "").trim();

  // Evita que Excel interprete datos del usuario como fórmulas.
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

const formatExcelDate = (dateValue) => {
  if (!dateValue) return "";

  const safeDate = String(dateValue).replace(" ", "T");
  const date = new Date(safeDate);

  if (Number.isNaN(date.getTime())) return makeExcelSafeText(dateValue);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

function BabiloniaUsersAdmin() {
  const [users, setUsers] = useState([]);
  const [agencias, setAgencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedAgency, setSelectedAgency] = useState("");
  const [selectedModule, setSelectedModule] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [agencyModalSearch, setAgencyModalSearch] = useState("");
  const [moduleModalSearch, setModuleModalSearch] = useState("");

  const [formData, setFormData] = useState({
    id: "",
    nombre: "",
    correo: "",
    telefono: "",
    activo: "1",
    agencias: [],
    modulos: [],
    contrasena: "",
    confirmar_contrasena: "",
  });

  const selectableModules = useMemo(
    () => MODULOS.filter((module) => module.selectable),
    [],
  );

  const moduleLabelMap = useMemo(() => {
    return MODULOS.reduce((acc, module) => {
      acc[module.name] = module.label;
      return acc;
    }, {});
  }, []);

  const agenciesLabelMap = useMemo(() => {
    return agencias.reduce((acc, agencia) => {
      acc[String(agencia.ag_id)] = agencia.nombre;
      return acc;
    }, {});
  }, [agencias]);

  const normalizeUser = (user) => ({
    ...user,
    id: String(user.id || ""),
    activo: String(user.activo ?? "1"),
    agencias: safeArray(user.agencias),
    modulos: safeArray(user.modulos),
  });

  const fetchAgencias = async () => {
    const response = await fetch(
      `${API_BASE_URL}agencies?access_token=${ACCESS_TOKEN}&group=GoCar`,
    );

    const result = await response.json();

    if (result.statusCode !== 200) {
      throw new Error(getResultMessage(result, "Error al obtener agencias."));
    }

    return result?.data?.agencies || [];
  };

  const fetchUsers = async () => {
    const response = await fetch(
      `${API_BASE_URL}financiamiento/users?access_token=${ACCESS_TOKEN}`,
    );

    const result = await response.json();

    if (result.statusCode !== 200) {
      throw new Error(getResultMessage(result, "Error al obtener usuarios."));
    }

    return (result?.data?.users || []).map(normalizeUser);
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [agenciasData, usersData] = await Promise.all([
        fetchAgencias(),
        fetchUsers(),
      ]);

      setAgencias(agenciasData);
      setUsers(usersData);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "No se pudo cargar la información.",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showModal) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [showModal]);

  const filteredUsers = useMemo(() => {
    const query = normalizeText(search);

    return users.filter((user) => {
      const nombre = normalizeText(user.nombre);
      const correo = normalizeText(user.correo);
      const telefono = normalizeText(user.telefono);
      const agenciasUsuario = safeArray(user.agencias);
      const modulosUsuario = safeArray(user.modulos);

      const agencyNames = agenciasUsuario
        .map((agId) => agenciesLabelMap[String(agId)] || "")
        .join(" ");

      const moduleNames = modulosUsuario
        .map((moduleName) => moduleLabelMap[moduleName] || moduleName)
        .join(" ");

      const matchesSearch =
        !query ||
        nombre.includes(query) ||
        correo.includes(query) ||
        telefono.includes(query) ||
        normalizeText(agencyNames).includes(query) ||
        normalizeText(moduleNames).includes(query);

      const matchesAgency =
        !selectedAgency || agenciasUsuario.includes(String(selectedAgency));

      const matchesModule =
        !selectedModule || modulosUsuario.includes(String(selectedModule));

      const matchesStatus =
        !selectedStatus ||
        String(user.activo ?? "1") === String(selectedStatus);

      return matchesSearch && matchesAgency && matchesModule && matchesStatus;
    });
  }, [
    users,
    search,
    selectedAgency,
    selectedModule,
    selectedStatus,
    agenciesLabelMap,
    moduleLabelMap,
  ]);

  const stats = useMemo(() => {
    const total = users.length;
    const active = users.filter((user) => isActiveUser(user.activo)).length;
    const inactive = total - active;
    const withAgencies = users.filter(
      (user) => safeArray(user.agencias).length,
    ).length;
    const admins = users.filter((user) =>
      safeArray(user.modulos).includes("admin"),
    ).length;

    return {
      total,
      visible: filteredUsers.length,
      active,
      inactive,
      withAgencies,
      admins,
    };
  }, [users, filteredUsers]);

  const filteredAgenciesForModal = useMemo(() => {
    const query = normalizeText(agencyModalSearch);

    if (!query) return agencias;

    return agencias.filter((agencia) =>
      normalizeText(agencia.nombre).includes(query),
    );
  }, [agencias, agencyModalSearch]);

  const filteredModulesForModal = useMemo(() => {
    const query = normalizeText(moduleModalSearch);

    if (!query) return selectableModules;

    return selectableModules.filter(
      (module) =>
        normalizeText(module.label).includes(query) ||
        normalizeText(module.name).includes(query),
    );
  }, [selectableModules, moduleModalSearch]);

  const openEditModal = (user) => {
    setFormData({
      id: String(user.id || ""),
      nombre: user.nombre || "",
      correo: user.correo || "",
      telefono: user.telefono || "",
      activo: String(user.activo ?? "1"),
      agencias: safeArray(user.agencias),
      modulos: safeArray(user.modulos),
      contrasena: "",
      confirmar_contrasena: "",
    });

    setAgencyModalSearch("");
    setModuleModalSearch("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (group, event) => {
    const { value, checked } = event.target;
    const parsedValue = String(value);

    setFormData((prev) => {
      const currentValues = safeArray(prev[group]);

      return {
        ...prev,
        [group]: checked
          ? Array.from(new Set([...currentValues, parsedValue]))
          : currentValues.filter((item) => item !== parsedValue),
      };
    });
  };

  const selectAllAgencias = () => {
    setFormData((prev) => ({
      ...prev,
      agencias: agencias.map((agencia) => String(agencia.ag_id)),
    }));
  };

  const clearAgencias = () => {
    setFormData((prev) => ({
      ...prev,
      agencias: [],
    }));
  };

  const selectVisibleAgencias = () => {
    setFormData((prev) => {
      const visibleIds = filteredAgenciesForModal.map((agencia) =>
        String(agencia.ag_id),
      );

      return {
        ...prev,
        agencias: Array.from(
          new Set([...safeArray(prev.agencias), ...visibleIds]),
        ),
      };
    });
  };

  const selectAllModules = () => {
    setFormData((prev) => ({
      ...prev,
      modulos: selectableModules.map((module) => module.name),
    }));
  };

  const clearModules = () => {
    setFormData((prev) => ({
      ...prev,
      modulos: [],
    }));
  };

  const selectVisibleModules = () => {
    setFormData((prev) => {
      const visibleModules = filteredModulesForModal.map(
        (module) => module.name,
      );

      return {
        ...prev,
        modulos: Array.from(
          new Set([...safeArray(prev.modulos), ...visibleModules]),
        ),
      };
    });
  };

  const clearPasswordFields = () => {
    setFormData((prev) => ({
      ...prev,
      contrasena: "",
      confirmar_contrasena: "",
    }));
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedAgency("");
    setSelectedModule("");
    setSelectedStatus("");
  };

  const exportActiveUsers = () => {
    try {
      const activeUsers = users
        .filter((user) => isActiveUser(user.activo))
        .sort((userA, userB) =>
          String(userA.nombre || "").localeCompare(
            String(userB.nombre || ""),
            "es",
            { sensitivity: "base" },
          ),
        );

      if (!activeUsers.length) {
        Swal.fire({
          icon: "info",
          title: "Sin usuarios activos",
          text: "No hay usuarios activos disponibles para exportar.",
        });
        return;
      }

      const rows = activeUsers.map((user) => {
        const agencyNames = safeArray(user.agencias).map(
          (agId) => agenciesLabelMap[String(agId)] || `Agencia ${agId}`,
        );

        return {
          Nombre: makeExcelSafeText(user.nombre || "Sin nombre"),
          Correo: makeExcelSafeText(user.correo || ""),
          Agencia: makeExcelSafeText(
            agencyNames.length ? agencyNames.join(", ") : "Sin agencia",
          ),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet["!cols"] = [
        { wch: 32 },
        { wch: 38 },
        { wch: 60 },
      ];

      if (worksheet["!ref"]) {
        worksheet["!autofilter"] = { ref: worksheet["!ref"] };
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Usuarios activos");

      const today = new Date();
      const fileDate = [
        today.getFullYear(),
        String(today.getMonth() + 1).padStart(2, "0"),
        String(today.getDate()).padStart(2, "0"),
      ].join("-");

      XLSX.writeFile(
        workbook,
        `usuarios_activos_babilonia_${fileDate}.xlsx`,
        { compression: true },
      );
    } catch (error) {
      console.error("Error al exportar usuarios activos:", error);

      Swal.fire({
        icon: "error",
        title: "No se pudo exportar",
        text: "Ocurrió un error al generar el archivo de Excel.",
      });
    }
  };

  const renderStatusBadge = (activo) => {
    const active = isActiveUser(activo);

    return (
      <span
        className={`bu-status ${active ? "bu-status-active" : "bu-status-inactive"}`}
      >
        <span className="bu-status-dot" />
        {active ? "Activo" : "Inactivo"}
      </span>
    );
  };

  const renderAgenciaBadges = (agenciasUsuario = [], limit = 2) => {
    const values = safeArray(agenciasUsuario);

    if (!values.length) {
      return <span className="bu-empty">Sin agencias</span>;
    }

    const visibleItems = values.slice(0, limit);
    const hiddenCount = values.length - visibleItems.length;

    return (
      <div className="bu-badge-wrap">
        {visibleItems.map((agId) => (
          <span className="bu-badge bu-badge-agency" key={agId}>
            {agenciesLabelMap[String(agId)] || `Agencia ${agId}`}
          </span>
        ))}

        {hiddenCount > 0 && (
          <span
            className="bu-badge bu-badge-more"
            title={values
              .map(
                (agId) => agenciesLabelMap[String(agId)] || `Agencia ${agId}`,
              )
              .join(", ")}
          >
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };

  const renderModuleBadges = (modules = [], limit = 3) => {
    const values = safeArray(modules);

    if (!values.length) {
      return <span className="bu-empty">Sin módulos</span>;
    }

    const visibleItems = values.slice(0, limit);
    const hiddenCount = values.length - visibleItems.length;

    return (
      <div className="bu-badge-wrap">
        {visibleItems.map((moduleName) => (
          <span
            className={`bu-badge ${
              moduleName === "admin" ? "bu-badge-admin" : "bu-badge-module"
            }`}
            key={moduleName}
          >
            {moduleLabelMap[moduleName] || moduleName}
          </span>
        ))}

        {hiddenCount > 0 && (
          <span
            className="bu-badge bu-badge-more"
            title={values
              .map((moduleName) => moduleLabelMap[moduleName] || moduleName)
              .join(", ")}
          >
            +{hiddenCount}
          </span>
        )}
      </div>
    );
  };

  const validateForm = () => {
    if (!formData.nombre.trim()) {
      Swal.fire("Campo requerido", "El nombre es obligatorio.", "warning");
      return false;
    }

    if (!formData.correo.trim()) {
      Swal.fire("Campo requerido", "El correo es obligatorio.", "warning");
      return false;
    }

    if (!formData.telefono.trim()) {
      Swal.fire("Campo requerido", "El teléfono es obligatorio.", "warning");
      return false;
    }

    const wantsPasswordChange =
      formData.contrasena.trim() !== "" ||
      formData.confirmar_contrasena.trim() !== "";

    if (wantsPasswordChange) {
      if (formData.contrasena !== formData.confirmar_contrasena) {
        Swal.fire(
          "Contraseña inválida",
          "Las contraseñas no coinciden.",
          "warning",
        );
        return false;
      }

      if (formData.contrasena.length < 6) {
        Swal.fire(
          "Contraseña inválida",
          "La contraseña debe tener al menos 6 caracteres.",
          "warning",
        );
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    const isDisablingUser = String(formData.activo) === "0";

    const confirm = await Swal.fire({
      icon: isDisablingUser ? "warning" : "question",
      title: isDisablingUser ? "¿Desactivar usuario?" : "¿Actualizar usuario?",
      text: isDisablingUser
        ? "El usuario quedará inactivo por borrado lógico. Podrás reactivarlo después."
        : "Se guardarán los cambios del usuario seleccionado.",
      showCancelButton: true,
      confirmButtonText: isDisablingUser ? "Sí, desactivar" : "Sí, actualizar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    setSaving(true);

    const payload = {
      access_token: ACCESS_TOKEN,
      id: formData.id,
      nombre: formData.nombre.trim(),
      correo: formData.correo.trim(),
      telefono: formData.telefono.trim(),
      activo: Number(formData.activo),
      agencias: formData.agencias,
      modulos: formData.modulos,
      contrasena: formData.contrasena,
      confirmar_contrasena: formData.confirmar_contrasena,
    };

    try {
      const response = await fetch(
        `${API_BASE_URL}financiamiento/users/update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      const result = await response.json();

      if (result.statusCode === 200) {
        Swal.fire({
          icon: "success",
          title: "Usuario actualizado",
          text: result.message || "El usuario se actualizó correctamente.",
        });

        setShowModal(false);
        await loadData();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: getResultMessage(result, "No se pudo actualizar el usuario."),
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: "Hubo un problema al conectar con el servidor.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>
        {`
          .bu-page {
            max-width: 1550px;
            margin: 0 auto;
          }

          .bu-hero {
            border: 1px solid rgba(148, 163, 184, 0.25);
            background:
              radial-gradient(circle at top left, rgba(0, 77, 138, 0.16), transparent 30%),
              linear-gradient(135deg, rgba(255,255,255,0.96), rgba(248,250,252,0.96));
            border-radius: 22px;
            padding: 24px;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.07);
          }

          .bu-title {
            font-weight: 800;
            letter-spacing: -0.03em;
            color: #0f172a;
          }

          .bu-subtitle {
            color: #64748b;
            font-size: 14px;
          }

          .bu-stat-card {
            border: 1px solid rgba(148, 163, 184, 0.22);
            border-radius: 18px;
            background: #ffffff;
            padding: 16px;
            height: 100%;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.06);
          }

          .bu-stat-label {
            color: #64748b;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            margin-bottom: 6px;
          }

          .bu-stat-value {
            font-size: 28px;
            font-weight: 800;
            color: #003b70;
            line-height: 1;
          }

          .bu-card {
            border: 1px solid rgba(148, 163, 184, 0.25);
            border-radius: 22px;
            background: #ffffff;
            box-shadow: 0 18px 45px rgba(15, 23, 42, 0.07);
            overflow: hidden;
          }

          .bu-filter-bar {
            padding: 18px;
            background: #f8fafc;
            border-bottom: 1px solid rgba(148, 163, 184, 0.22);
          }

          .bu-input-icon {
            position: relative;
          }

          .bu-input-icon i {
            position: absolute;
            left: 14px;
            top: 50%;
            transform: translateY(-50%);
            color: #64748b;
            font-size: 14px;
          }

          .bu-input-icon input {
            padding-left: 40px;
          }

          .bu-control {
            border-radius: 13px;
            border: 1px solid #dbe3ee;
            min-height: 42px;
            font-size: 14px;
          }

          .bu-control:focus {
            border-color: #004987;
            box-shadow: 0 0 0 0.18rem rgba(0, 73, 135, 0.14);
          }

          .bu-table-wrap {
            max-height: calc(100vh - 360px);
            overflow: auto;
          }

          .bu-table {
            margin-bottom: 0;
          }

          .bu-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #ffffff;
            color: #475569;
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.07em;
            border-bottom: 1px solid #e2e8f0;
            padding: 14px 16px;
            white-space: nowrap;
          }

          .bu-table tbody td {
            padding: 16px;
            vertical-align: middle;
            border-color: #edf2f7;
          }

          .bu-table tbody tr {
            transition: background 0.15s ease, opacity 0.15s ease;
          }

          .bu-table tbody tr:hover {
            background: #f8fbff;
          }

          .bu-row-inactive {
            opacity: 0.72;
            background: #fffafa;
          }

          .bu-user-cell {
            display: flex;
            align-items: center;
            gap: 12px;
            min-width: 260px;
          }

          .bu-avatar {
            width: 42px;
            height: 42px;
            border-radius: 14px;
            background: linear-gradient(135deg, #004987, #0ea5e9);
            color: #ffffff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 800;
            flex: 0 0 auto;
            box-shadow: 0 8px 20px rgba(0, 73, 135, 0.22);
          }

          .bu-avatar-inactive {
            background: linear-gradient(135deg, #64748b, #94a3b8);
            box-shadow: none;
          }

          .bu-user-name {
            font-weight: 750;
            color: #0f172a;
            line-height: 1.2;
          }

          .bu-user-email {
            color: #64748b;
            font-size: 12px;
            margin-top: 3px;
            max-width: 260px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .bu-user-id {
            font-size: 12px;
            color: #64748b;
            background: #f1f5f9;
            border: 1px solid #e2e8f0;
            padding: 4px 8px;
            border-radius: 999px;
            display: inline-flex;
            align-items: center;
            gap: 4px;
          }

          .bu-badge-wrap {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            max-width: 420px;
          }

          .bu-badge {
            display: inline-flex;
            align-items: center;
            border-radius: 999px;
            padding: 5px 9px;
            font-size: 11px;
            font-weight: 700;
            line-height: 1;
            border: 1px solid transparent;
            white-space: nowrap;
          }

          .bu-badge-agency {
            color: #075985;
            background: #e0f2fe;
            border-color: #bae6fd;
          }

          .bu-badge-module {
            color: #334155;
            background: #f1f5f9;
            border-color: #e2e8f0;
          }

          .bu-badge-admin {
            color: #7c2d12;
            background: #ffedd5;
            border-color: #fed7aa;
          }

          .bu-badge-more {
            color: #003b70;
            background: #eaf4ff;
            border-color: #c7ddf2;
          }

          .bu-empty {
            color: #94a3b8;
            font-size: 12px;
            font-style: italic;
          }

          .bu-action-btn {
            border-radius: 12px;
            font-weight: 700;
            padding: 8px 12px;
          }

          .bu-status {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            border-radius: 999px;
            padding: 6px 10px;
            font-size: 11px;
            font-weight: 800;
            line-height: 1;
            border: 1px solid transparent;
            white-space: nowrap;
          }

          .bu-status-dot {
            width: 7px;
            height: 7px;
            border-radius: 999px;
            background: currentColor;
          }

          .bu-status-active {
            color: #166534;
            background: #dcfce7;
            border-color: #bbf7d0;
          }

          .bu-status-inactive {
            color: #991b1b;
            background: #fee2e2;
            border-color: #fecaca;
          }

          .bu-modal-backdrop {
            position: fixed;
            inset: 0;
            z-index: 1060;
            background: rgba(15, 23, 42, 0.64);
            backdrop-filter: blur(4px);
            display: flex;
            align-items: flex-start;
            justify-content: center;
            padding: 24px;
            overflow-y: auto;
            overflow-x: hidden;
          }

          .bu-modal-panel {
            width: min(1180px, 100%);
            height: calc(100vh - 48px);
            max-height: calc(100vh - 48px);
            min-height: 0;
            background: #ffffff;
            border-radius: 24px;
            box-shadow: 0 30px 90px rgba(15, 23, 42, 0.34);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          .bu-modal-panel form {
            height: 100%;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }

          .bu-modal-header {
            flex: 0 0 auto;
            padding: 22px 26px;
            background:
              radial-gradient(circle at top left, rgba(14,165,233,0.22), transparent 28%),
              linear-gradient(135deg, #003b70, #005ea8);
            color: #ffffff;
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 16px;
          }

          .bu-modal-title {
            font-size: 21px;
            font-weight: 850;
            margin: 0;
            letter-spacing: -0.02em;
          }

          .bu-modal-subtitle {
            color: rgba(255,255,255,0.78);
            margin-top: 4px;
            font-size: 13px;
          }

          .bu-modal-close {
            width: 38px;
            height: 38px;
            border: 0;
            border-radius: 12px;
            background: rgba(255,255,255,0.14);
            color: #ffffff;
            font-size: 24px;
            line-height: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .bu-modal-close:hover {
            background: rgba(255,255,255,0.22);
          }

          .bu-modal-body {
            flex: 1 1 auto;
            min-height: 0;
            padding: 22px;
            overflow-y: auto;
            overflow-x: hidden;
            background: #f8fafc;
          }

          .bu-modal-footer {
            flex: 0 0 auto;
            position: sticky;
            bottom: 0;
            z-index: 3;
            padding: 16px 22px;
            background: #ffffff;
            border-top: 1px solid #e2e8f0;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
          }

          .bu-modal-footer-actions {
            display: flex;
            gap: 10px;
          }

          .bu-section {
            border: 1px solid #e2e8f0;
            border-radius: 20px;
            background: #ffffff;
            padding: 18px;
            margin-bottom: 16px;
            box-shadow: 0 8px 22px rgba(15, 23, 42, 0.035);
          }

          .bu-section-title {
            font-weight: 800;
            color: #0f172a;
            margin-bottom: 3px;
          }

          .bu-section-help {
            color: #64748b;
            font-size: 13px;
            margin-bottom: 16px;
          }

          .bu-warning-card {
            border: 1px solid #fed7aa;
            background: #fff7ed;
            color: #9a3412;
            border-radius: 16px;
            padding: 12px 14px;
            font-size: 13px;
            font-weight: 650;
          }

          .bu-check-grid {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 10px;
          }

          .bu-check-card {
            position: relative;
          }

          .bu-check-card input {
            position: absolute;
            opacity: 0;
            pointer-events: none;
          }

          .bu-check-card label {
            width: 100%;
            min-height: 44px;
            border: 1px solid #dbe3ee;
            border-radius: 14px;
            background: #ffffff;
            padding: 11px 12px;
            font-size: 13px;
            font-weight: 650;
            color: #334155;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 9px;
            transition: all 0.15s ease;
          }

          .bu-check-card label::before {
            content: "";
            width: 16px;
            height: 16px;
            border-radius: 5px;
            border: 1px solid #94a3b8;
            background: #ffffff;
            flex: 0 0 auto;
          }

          .bu-check-card input:checked + label {
            color: #003b70;
            background: #eef7ff;
            border-color: #7ab6e8;
            box-shadow: 0 8px 18px rgba(0, 73, 135, 0.08);
          }

          .bu-check-card input:checked + label::before {
            border-color: #004987;
            background: #004987;
            box-shadow: inset 0 0 0 3px #ffffff;
          }

          .bu-password-note {
            border: 1px solid #bae6fd;
            background: #effaff;
            color: #0369a1;
            border-radius: 16px;
            padding: 12px 14px;
            font-size: 13px;
            font-weight: 650;
          }

          .bu-mini-btn {
            border-radius: 12px;
            font-weight: 800;
            padding: 8px 12px;
            font-size: 12px;
            min-height: 34px;
          }

          .bu-btn-clear {
            color: #334155;
            border: 1px solid #cbd5e1;
            background: #ffffff;
          }

          .bu-btn-clear:hover {
            color: #0f172a;
            border-color: #94a3b8;
            background: #f8fafc;
          }

          .bu-btn-primary-soft {
            color: #003b70;
            border: 1px solid #b7d9f4;
            background: #eef7ff;
          }

          .bu-btn-primary-soft:hover {
            color: #ffffff;
            border-color: #004987;
            background: #004987;
          }

          @media (max-width: 991px) {
            .bu-check-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .bu-table-wrap {
              max-height: none;
            }
          }

          @media (max-width: 575px) {
            .bu-hero {
              padding: 18px;
            }

            .bu-check-grid {
              grid-template-columns: 1fr;
            }

            .bu-modal-backdrop {
              padding: 10px;
            }

            .bu-modal-panel {
              height: calc(100vh - 20px);
              max-height: calc(100vh - 20px);
              border-radius: 18px;
            }

            .bu-modal-header,
            .bu-modal-body,
            .bu-modal-footer {
              padding-left: 16px;
              padding-right: 16px;
            }

            .bu-modal-footer {
              flex-direction: column;
              align-items: stretch;
            }

            .bu-modal-footer-actions {
              width: 100%;
              flex-direction: column;
            }

            .bu-modal-footer-actions .btn {
              width: 100%;
            }
          }
        `}
      </style>

      <div className="container-fluid py-4 bu-page">
        <div className="bu-hero mb-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
            <div>
              <h1 className="bu-title h3 mb-1">Usuarios Babilonia</h1>
              <div className="bu-subtitle">
                Administra usuarios, agencias, módulos, permisos, estatus y
                contraseña.
              </div>
            </div>

            <div className="d-flex align-items-start gap-2 flex-wrap">
              <button
                type="button"
                className="btn btn-success"
                onClick={exportActiveUsers}
                disabled={loading || stats.active === 0}
                title="Exporta todos los usuarios activos y sus agencias"
              >
                <i className="fa fa-file-excel-o me-2" />
                Exportar activos
              </button>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={clearFilters}
                disabled={loading}
              >
                Limpiar filtros
              </button>

              <button
                type="button"
                className="btn btn-theme"
                onClick={loadData}
                disabled={loading}
              >
                {loading ? "Cargando..." : "Actualizar listado"}
              </button>
            </div>
          </div>

          <div className="row g-3 mt-3">
            <div className="col-sm-6 col-xl-3">
              <div className="bu-stat-card">
                <div className="bu-stat-label">Usuarios totales</div>
                <div className="bu-stat-value">{stats.total}</div>
              </div>
            </div>

            <div className="col-sm-6 col-xl-3">
              <div className="bu-stat-card">
                <div className="bu-stat-label">Resultado actual</div>
                <div className="bu-stat-value">{stats.visible}</div>
              </div>
            </div>

            <div className="col-sm-6 col-xl-3">
              <div className="bu-stat-card">
                <div className="bu-stat-label">Activos</div>
                <div className="bu-stat-value">{stats.active}</div>
              </div>
            </div>

            <div className="col-sm-6 col-xl-3">
              <div className="bu-stat-card">
                <div className="bu-stat-label">Inactivos</div>
                <div className="bu-stat-value">{stats.inactive}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bu-card">
          <div className="bu-filter-bar">
            <div className="row g-3 align-items-end">
              <div className="col-lg-4">
                <label className="form-label small text-muted mb-1">
                  Buscar usuario
                </label>
                <div className="bu-input-icon">
                  <i className="fa fa-search" />
                  <input
                    type="text"
                    className="form-control bu-control"
                    placeholder="Nombre, correo, teléfono, agencia o módulo..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="col-md-6 col-lg-3">
                <label className="form-label small text-muted mb-1">
                  Filtrar por agencia
                </label>
                <select
                  className="form-select bu-control"
                  value={selectedAgency}
                  onChange={(event) => setSelectedAgency(event.target.value)}
                >
                  <option value="">Todas las agencias</option>
                  {agencias.map((agencia) => (
                    <option key={agencia.ag_id} value={String(agencia.ag_id)}>
                      {agencia.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 col-lg-2">
                <label className="form-label small text-muted mb-1">
                  Filtrar por módulo
                </label>
                <select
                  className="form-select bu-control"
                  value={selectedModule}
                  onChange={(event) => setSelectedModule(event.target.value)}
                >
                  <option value="">Todos</option>
                  {selectableModules.map((module) => (
                    <option key={module.name} value={module.name}>
                      {module.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 col-lg-2">
                <label className="form-label small text-muted mb-1">
                  Estatus
                </label>
                <select
                  className="form-select bu-control"
                  value={selectedStatus}
                  onChange={(event) => setSelectedStatus(event.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="1">Activos</option>
                  <option value="0">Inactivos</option>
                </select>
              </div>

              <div className="col-md-6 col-lg-1 text-lg-end">
                <span className="bu-user-id">{filteredUsers.length}</span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-5 text-center text-body text-opacity-50">
              Cargando usuarios...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="p-5 text-center">
              <div className="h5 mb-1">No se encontraron usuarios</div>
              <div className="text-body text-opacity-50">
                Intenta limpiar los filtros o buscar otro dato.
              </div>
            </div>
          ) : (
            <div className="bu-table-wrap">
              <table className="table table-hover align-middle bu-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Estatus</th>
                    <th>Teléfono</th>
                    <th>Agencias</th>
                    <th>Módulos</th>
                    <th>Creado</th>
                    <th className="text-end">Acción</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => {
                    const active = isActiveUser(user.activo);

                    return (
                      <tr
                        key={user.id}
                        className={!active ? "bu-row-inactive" : ""}
                      >
                        <td>
                          <div className="bu-user-cell">
                            <div
                              className={`bu-avatar ${
                                !active ? "bu-avatar-inactive" : ""
                              }`}
                            >
                              {getInitials(user.nombre)}
                            </div>

                            <div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="bu-user-name">
                                  {user.nombre || "Sin nombre"}
                                </span>
                                <span className="bu-user-id">ID {user.id}</span>
                              </div>

                              <div
                                className="bu-user-email"
                                title={user.correo}
                              >
                                {user.correo || "Sin correo"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td>{renderStatusBadge(user.activo)}</td>

                        <td>
                          <span className="text-body">
                            {user.telefono || "-"}
                          </span>
                        </td>

                        <td>{renderAgenciaBadges(user.agencias)}</td>

                        <td>{renderModuleBadges(user.modulos)}</td>

                        <td>
                          <span className="text-muted small">
                            {formatDate(user.created_at)}
                          </span>
                        </td>

                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-primary bu-action-btn"
                            onClick={() => openEditModal(user)}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="bu-modal-backdrop" onMouseDown={closeModal}>
            <div
              className="bu-modal-panel"
              role="dialog"
              aria-modal="true"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <form onSubmit={handleSubmit}>
                <div className="bu-modal-header">
                  <div>
                    <h5 className="bu-modal-title">Editar usuario</h5>
                    <div className="bu-modal-subtitle">
                      ID usuario: {formData.id} ·{" "}
                      {formData.correo || "Sin correo"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="bu-modal-close"
                    onClick={closeModal}
                    disabled={saving}
                    aria-label="Cerrar"
                  >
                    ×
                  </button>
                </div>

                <div className="bu-modal-body">
                  <div className="bu-section">
                    <div className="bu-section-title">
                      Información del usuario
                    </div>
                    <div className="bu-section-help">
                      Datos principales para identificar al usuario dentro de
                      Babilonia.
                    </div>

                    {String(formData.activo) === "0" && (
                      <div className="bu-warning-card mb-3">
                        Este usuario está inactivo por borrado lógico. Puede
                        reactivarse cambiando el estatus a Activo.
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="bu-field-label">
                          Nombre <span className="bu-required">*</span>
                        </label>
                        <input
                          type="text"
                          name="nombre"
                          className="form-control bu-control"
                          value={formData.nombre}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="bu-field-label">
                          Correo <span className="bu-required">*</span>
                        </label>
                        <input
                          type="email"
                          name="correo"
                          className="form-control bu-control"
                          value={formData.correo}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="bu-field-label">
                          Teléfono <span className="bu-required">*</span>
                        </label>
                        <input
                          type="text"
                          name="telefono"
                          className="form-control bu-control"
                          value={formData.telefono}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-md-3">
                        <label className="bu-field-label">
                          Estatus <span className="bu-required">*</span>
                        </label>
                        <select
                          name="activo"
                          className="form-select bu-control"
                          value={formData.activo}
                          onChange={handleChange}
                          required
                        >
                          <option value="1">Activo</option>
                          <option value="0">Inactivo / borrado lógico</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="bu-section">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
                      <div>
                        <div className="bu-section-title">Contraseña</div>
                        <div className="bu-section-help mb-0">
                          Solo llena estos campos cuando necesites cambiar la
                          contraseña del usuario.
                        </div>
                      </div>

                      <div>
                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-clear"
                          onClick={clearPasswordFields}
                        >
                          Limpiar contraseña
                        </button>
                      </div>
                    </div>

                    <div className="bu-password-note mb-3">
                      Deja la contraseña vacía si no quieres cambiarla.
                    </div>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="bu-field-label">
                          Nueva contraseña
                        </label>
                        <input
                          type="password"
                          name="contrasena"
                          className="form-control bu-control"
                          value={formData.contrasena}
                          onChange={handleChange}
                          autoComplete="new-password"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="bu-field-label">
                          Confirmar nueva contraseña
                        </label>
                        <input
                          type="password"
                          name="confirmar_contrasena"
                          className="form-control bu-control"
                          value={formData.confirmar_contrasena}
                          onChange={handleChange}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bu-section">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
                      <div>
                        <div className="bu-section-title">
                          Agencias asignadas
                        </div>
                        <div className="bu-section-help mb-0">
                          Seleccionadas: {formData.agencias.length}
                        </div>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-primary-soft"
                          onClick={selectVisibleAgencias}
                        >
                          Seleccionar visibles
                        </button>

                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-primary-soft"
                          onClick={selectAllAgencias}
                        >
                          Todas
                        </button>

                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-clear"
                          onClick={clearAgencias}
                        >
                          Limpiar
                        </button>
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <div className="bu-input-icon">
                          <i className="fa fa-search" />
                          <input
                            type="text"
                            className="form-control bu-control"
                            placeholder="Buscar agencia dentro del modal..."
                            value={agencyModalSearch}
                            onChange={(event) =>
                              setAgencyModalSearch(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {filteredAgenciesForModal.length === 0 ? (
                      <div className="text-muted small">
                        No se encontraron agencias con ese texto.
                      </div>
                    ) : (
                      <div className="bu-check-grid">
                        {filteredAgenciesForModal.map((agencia) => {
                          const agenciaId = String(agencia.ag_id);

                          return (
                            <div className="bu-check-card" key={agenciaId}>
                              <input
                                type="checkbox"
                                id={`edit-agencia-${agenciaId}`}
                                value={agenciaId}
                                checked={formData.agencias.includes(agenciaId)}
                                onChange={(event) =>
                                  handleCheckboxChange("agencias", event)
                                }
                              />
                              <label htmlFor={`edit-agencia-${agenciaId}`}>
                                {agencia.nombre}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="bu-section mb-0">
                    <div className="d-flex flex-column flex-md-row justify-content-between gap-3 mb-3">
                      <div>
                        <div className="bu-section-title">
                          Módulos asignados
                        </div>
                        <div className="bu-section-help mb-0">
                          Seleccionados: {formData.modulos.length}
                        </div>
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-primary-soft"
                          onClick={selectVisibleModules}
                        >
                          Seleccionar visibles
                        </button>

                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-primary-soft"
                          onClick={selectAllModules}
                        >
                          Todos
                        </button>

                        <button
                          type="button"
                          className="bu-mini-btn bu-btn-clear"
                          onClick={clearModules}
                        >
                          Limpiar
                        </button>
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <div className="bu-input-icon">
                          <i className="fa fa-search" />
                          <input
                            type="text"
                            className="form-control bu-control"
                            placeholder="Buscar módulo dentro del modal..."
                            value={moduleModalSearch}
                            onChange={(event) =>
                              setModuleModalSearch(event.target.value)
                            }
                          />
                        </div>
                      </div>
                    </div>

                    {filteredModulesForModal.length === 0 ? (
                      <div className="text-muted small">
                        No se encontraron módulos con ese texto.
                      </div>
                    ) : (
                      <div className="bu-check-grid">
                        {filteredModulesForModal.map((module) => (
                          <div className="bu-check-card" key={module.name}>
                            <input
                              type="checkbox"
                              id={`edit-module-${module.name}`}
                              value={module.name}
                              checked={formData.modulos.includes(module.name)}
                              onChange={(event) =>
                                handleCheckboxChange("modulos", event)
                              }
                            />
                            <label htmlFor={`edit-module-${module.name}`}>
                              {module.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="bu-modal-footer">
                  <div>{renderStatusBadge(formData.activo)}</div>

                  <div className="bu-modal-footer-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                      disabled={saving}
                    >
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      className="btn btn-theme"
                      disabled={saving}
                    >
                      {saving ? "Guardando..." : "Guardar cambios"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default BabiloniaUsersAdmin;
